import { useEffect, useRef, useState } from "react";

import { OptionNameValuePair } from "../../../../lib/interfaces/types";

import {
  buildEmployeeTypeOptions,
  INLINE_ROW_LIMIT,
  Employee,
  relatedAsLabel,
} from "../../../../api/creditAccounts";
import { getEmployeeRowErrors } from "../../utils/creditValidation";
import { SectionIssue } from "./derive";
import FormField, { useTouched } from "./FormField";
import { SectionAccordion, CollectionModal, ViewMoreButton } from "./SectionShell";

interface RowProps {
  row: Employee;
  typeOptions: OptionNameValuePair<string>[];
  showErrors: boolean;
  onIdChange: (employeeId: string) => void;
}

// Employees are DERIVED from employee-type Primary Parties: Type and Name are
// locked and edited on the originating party. Only the Employee ID is editable
// here, and it is mandatory.
const EmployeeRow: React.FC<RowProps> = ({ row, typeOptions, showErrors, onIdChange }) => {
  const { isTouched, markTouched } = useTouched();
  const errors = getEmployeeRowErrors(row);
  const err = (f: string) => (showErrors || isTouched(f) ? errors[f] : undefined);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
      <FormField type="text" label="Type" required placeholder="Select type"
        value={row.types.map(v => relatedAsLabel(v)).join(", ")} disabled error={err("type")}
        onChange={() => {}} />
      <FormField type="text" label="Employee Name" required placeholder="Enter name"
        value={row.name} disabled error={err("name")}
        onChange={() => {}} />
      <FormField type="text" label="Employee ID" required placeholder="Enter employee ID"
        value={row.employeeId ?? ""} error={err("employeeId")}
        onChange={onIdChange} onBlur={() => markTouched("employeeId")} />
    </div>
  );
};

interface EmployeeSectionProps {
  employees: Employee[]; // derived only
  onEmployeeIdChange: (key: string, employeeId: string) => void;
  showErrors: boolean;
  issue: SectionIssue;
  focus: { key: string; seq: number };
}

const EmployeeSection: React.FC<EmployeeSectionProps> = ({
  employees,
  onEmployeeIdChange,
  showErrors,
  issue,
  focus,
}) => {
  const typeOptions = buildEmployeeTypeOptions();
  const [modalOpen, setModalOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focus.key !== "employees") return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (issue.hiddenInvalid > 0) setModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus.seq]);

  const renderRows = (list: Employee[]) => (
    <div className="flex flex-col gap-4">
      {list.map((row) => (
        <EmployeeRow key={row.key} row={row} typeOptions={typeOptions}
          showErrors={showErrors}
          onIdChange={(v) => onEmployeeIdChange(row.key, v)} />
      ))}
    </div>
  );

  return (
    <>
      <div ref={rootRef}>
        <SectionAccordion title="Employee" count={employees.length}
          invalidCount={showErrors ? issue.incompleteRows : 0} onExpand={() => setModalOpen(true)}>
          <div className="flex flex-col gap-4">
            {employees.length === 0 ? (
              <p className="text-sm text-gray-500">
                Employees are added automatically from employee-type Primary Parties.
              </p>
            ) : (
              renderRows(employees.slice(0, INLINE_ROW_LIMIT))
            )}
            {employees.length > INLINE_ROW_LIMIT && (
              <div className="flex items-center justify-end">
                <ViewMoreButton overflowCount={employees.length - INLINE_ROW_LIMIT}
                  singular="employee" plural="employees"
                  invalidCount={showErrors ? issue.hiddenInvalid : 0}
                  onClick={() => setModalOpen(true)} />
              </div>
            )}
          </div>
        </SectionAccordion>
      </div>

      {modalOpen && (
        <CollectionModal title="Employee" onClose={() => setModalOpen(false)} onDone={() => setModalOpen(false)}>
          <div className="flex flex-col gap-4">
            {renderRows(employees)}
          </div>
        </CollectionModal>
      )}
    </>
  );
};

export default EmployeeSection;
